// Phase 15 M1 — Web auth store, parity with mobile/src/store/auth.ts.
//
// Bearer tokens live in `localStorage` (NOT cookies). Rationale (PRD §M1):
// the backend `authenticate` middleware already expects
// `Authorization: Bearer <jwt>`, identical to mobile. Cookies would force a
// second auth path. localStorage is fine here — the marketplace SPA is served
// from chain.leyro.es and the API from api.chain.leyro.es; XSS on the SPA
// would already compromise the user regardless of token location.
//
// The store is intentionally minimal: no role switcher (web only has BUYER
// flows; the FARMER/VET/TRANSPORTISTA navigators are mobile-only), no
// onboarding gate (V1 web tells the user to finish onboarding on mobile and
// only opens BUYER routes). `needs_onboarding` is surfaced as a banner toast
// upstream rather than blocking navigation.

import type { AuthenticatedUser, TokenPair } from '@leyrochain/shared';

export const ACCESS_TOKEN_KEY = 'leyrochain.web.access_token';
export const REFRESH_TOKEN_KEY = 'leyrochain.web.refresh_token';
export const USER_KEY = 'leyrochain.web.user';
export const NEEDS_ONBOARDING_KEY = 'leyrochain.web.needs_onboarding';

export interface AuthSnapshot {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthenticatedUser | null;
  needsOnboarding: boolean;
}

type Listener = (snapshot: AuthSnapshot) => void;

function readUser(storage: Storage): AuthenticatedUser | null {
  const raw = storage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthenticatedUser;
  } catch {
    return null;
  }
}

function readSnapshot(storage: Storage): AuthSnapshot {
  return {
    accessToken: storage.getItem(ACCESS_TOKEN_KEY),
    refreshToken: storage.getItem(REFRESH_TOKEN_KEY),
    user: readUser(storage),
    needsOnboarding: storage.getItem(NEEDS_ONBOARDING_KEY) === '1',
  };
}

function resolveStorage(): Storage {
  // SSR / vitest jsdom / Node 22 fallback. Node 22 exposes a
  // `globalThis.localStorage` experimental shim that throws on read
  // when `--localstorage-file` is not passed, and jsdom may not have
  // wired window.localStorage yet during module-level evaluation.
  // We treat any non-functional storage as "use the in-memory shim".
  const candidate =
    typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  if (candidate && typeof candidate.getItem === 'function') {
    try {
      // Smoke test — Node 22's shim throws here.
      candidate.getItem('__store_probe__');
      return candidate;
    } catch {
      // Fall through to in-memory shim.
    }
  }
  const map = new Map<string, string>();
  return {
    get length() {
      return map.size;
    },
    clear: () => map.clear(),
    getItem: (k) => map.get(k) ?? null,
    key: (i) => Array.from(map.keys())[i] ?? null,
    removeItem: (k) => map.delete(k),
    setItem: (k, v) => map.set(k, v),
  } as Storage;
}

export interface AuthStore {
  getSnapshot: () => AuthSnapshot;
  setSession: (tokens: TokenPair) => void;
  setUser: (user: AuthenticatedUser) => void;
  logout: () => void;
  subscribe: (listener: Listener) => () => void;
}

export function createAuthStore(storage: Storage = resolveStorage()): AuthStore {
  let snapshot = readSnapshot(storage);
  const listeners = new Set<Listener>();

  function emit(): void {
    snapshot = readSnapshot(storage);
    listeners.forEach((l) => l(snapshot));
  }

  return {
    getSnapshot: () => snapshot,
    setSession: (tokens) => {
      storage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
      storage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
      storage.setItem(USER_KEY, JSON.stringify(tokens.user));
      if (tokens.needs_onboarding) {
        storage.setItem(NEEDS_ONBOARDING_KEY, '1');
      } else {
        storage.removeItem(NEEDS_ONBOARDING_KEY);
      }
      emit();
    },
    setUser: (user) => {
      storage.setItem(USER_KEY, JSON.stringify(user));
      emit();
    },
    logout: () => {
      storage.removeItem(ACCESS_TOKEN_KEY);
      storage.removeItem(REFRESH_TOKEN_KEY);
      storage.removeItem(USER_KEY);
      storage.removeItem(NEEDS_ONBOARDING_KEY);
      emit();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

// Module-level singleton: every consumer (axios client, React hooks, route
// guards) reads from the same instance so a logout in one place reflects
// everywhere immediately. Tests can build isolated stores via createAuthStore.
export const authStore = createAuthStore();
